/**
 * Receitas numeradas pelo SNCR: RET (antimicrobiano, Fase 1) e RCE (controle
 * especial, Fase 2) em blocos de 1.000; NRA, NRB e NRB2 (Notificações de
 * Receita, Fase 3) em pedidos de 10 a 50 números autorizados pela vigilância
 * sanitária. Saldo e limites são separados por tipo.
 */
export type SncrRecipeType = 'RET' | 'RCE' | 'NRA' | 'NRB' | 'NRB2'

export const SNCR_RECIPE_TYPES: readonly SncrRecipeType[] = [
  'RET',
  'RCE',
  'NRA',
  'NRB',
  'NRB2'
]

/** Nome da receita nas mensagens ao médico, no plural (espelho do SncrRecipeType::label do backend). */
export const SNCR_RECIPE_LABEL: Readonly<Record<SncrRecipeType, string>> = {
  RET: 'receitas de antimicrobiano',
  RCE: 'receitas de controle especial',
  NRA: 'notificações de receita A',
  NRB: 'notificações de receita B',
  NRB2: 'notificações de receita B2'
}

/** Numeração de Notificação de Receita (números avulsos, autorizados pela vigilância). */
export function isSncrNotification(type: SncrRecipeType): boolean {
  return type === 'NRA' || type === 'NRB' || type === 'NRB2'
}

/** Notificação da letra do item da prescrição. */
export const SNCR_NOTIFICATION_TYPE: Readonly<
  Record<'A' | 'B' | 'B2', SncrRecipeType>
> = { A: 'NRA', B: 'NRB', B2: 'NRB2' }

/** Códigos que o backend devolve (422) quando a receita precisa de número do SNCR. */
export type SncrErrorCode =
  // finalização (POST document/finishAttendance)
  | 'SNCR_SEM_SALDO'
  | 'SNCR_SEM_CRM'
  // aviso: notificação retirada porque a finalização não foi assinada
  | 'SNCR_NOTIFICACAO_SEM_ASSINATURA'
  // pedido de bloco (POST sncr/blocks)
  | 'SNCR_GOVBR_EXPIRADO'
  | 'SNCR_PRESCRITOR_NAO_CADASTRADO'
  | 'SNCR_CRM_SEM_VINCULO'
  | 'SNCR_LIMITE_MENSAL'
  | 'SNCR_LIMITE_DIARIO'
  | 'SNCR_SEM_AUTORIZACAO_VISA'
  | 'SNCR_INDISPONIVEL'
  | 'SNCR_RESPOSTA_NAO_RECONHECIDA'
  | 'SNCR_RECUSADO'

export interface SncrError {
  code: SncrErrorCode
  message: string
  /** Qual numeração faltou; ausente em resposta antiga (= RET). */
  recipeType?: SncrRecipeType
  /**
   * Com `sncrMode: 'ask'`, a finalização parou ANTES de gerar os documentos e
   * o médico pode seguir: RET/RCE saem sem número (até 30/10/2026); a
   * notificação sai da finalização (talonário de papel), em qualquer data.
   */
  canEmitWithoutNumber?: boolean
}

export interface SncrBlock {
  id: string
  first: string
  last: string
  remaining: number
  obtained_at: string | null
}

/** Limites do pedido de números de notificação (GET sncr/balance de NRA/NRB/NRB2). */
export interface SncrNotificationLimits {
  min_quantity: number
  max_quantity: number
  daily_limit: number
  /** Números do tipo que o BI pediu hoje (outras plataformas também consomem o limite). */
  obtained_today: number
  /** Saldo autorizado que o SNCR informou no último pedido; null = ainda não pedido. */
  sncr_balance: number | null
  sncr_balance_at: string | null
}

/** GET sncr/balance */
export interface SncrBalance {
  recipe_type: SncrRecipeType
  available: number
  /** Só nas notificações; null em RET/RCE (ausente em resposta antiga). */
  notification?: SncrNotificationLimits | null
  crm: { state: string; number: string } | null
  environment: string
  mandatory: boolean
  mandatory_from: string
  blocks: SncrBlock[]
}

/**
 * `sncr_warning` da resposta de finishAttendance: a receita saiu SEM número
 * no período de transição (até 30/10/2026). A finalização não é bloqueada.
 */
export interface SncrWarning extends Omit<SncrError, 'recipeType'> {
  mandatory_from: string | null
  recipe_type?: SncrRecipeType
}

/** Detalhe do evento que abre o diálogo de numeração. */
export interface SncrNumberingRequest extends SncrError {
  professionalId?: string | null
  /**
   * Aviso do período de transição: a finalização já aconteceu (sem número) e
   * o diálogo só convida a obter a numeração. Ausente = a finalização foi
   * bloqueada e precisa ser refeita.
   */
  notice?: boolean
  /**
   * Pedido antecipado pela prescrição (faixa de saldo), antes de finalizar:
   * não há finalização a refazer.
   */
  prefetch?: boolean
  /**
   * Aviso que não pode ser dispensado por 24 h: a notificação de receita não
   * foi gerada, e o médico precisa saber a cada finalização.
   */
  persistent?: boolean
  /** Refaz a finalização emitindo sem número, ou sem a notificação (`sncrMode: 'skip'`). */
  onEmitWithoutNumber?: () => void
  /** Refaz a finalização (depois de obter os números no gov.br). */
  onRetry?: () => void
}
